import { bytesEqual, fromBase64Url, fromUtf8, toBase64Url, utf8, } from './bytes.js';
import { exactKeys, openContent, sealContent, validateContentContext, } from './content.js';
import { sha256 } from './hash.js';
import { canonicalize } from './jcs.js';
export const ARTIFACT_CHUNK_BYTES = 1024 * 1024;
export const MAX_ARTIFACT_CHUNKS = 256;
function context(base, kind, seq) {
    exactKeys(base, ['version', 'accountId', 'epoch', 'objectId', 'runId']);
    const c = { ...base, kind, seq };
    validateContentContext(c);
    return c;
}
/** Encrypt before upload. No names, MIME, plaintext digest or exact length leave the endpoint. */
export async function sealArtifact(key, base, bytes, metadata) {
    if (bytes.length > ARTIFACT_CHUNK_BYTES * MAX_ARTIFACT_CHUNKS)
        throw new Error('artifact too large');
    const chunks = [];
    for (let offset = 0; offset < bytes.length; offset += ARTIFACT_CHUNK_BYTES) {
        chunks.push(await sealContent(key, context(base, 'artifact.chunk', chunks.length + 1), bytes.subarray(offset, offset + ARTIFACT_CHUNK_BYTES)));
    }
    const manifest = {
        version: 1,
        name: metadata.name,
        mime: metadata.mime,
        bytes: bytes.length,
        chunks: chunks.length,
        digest: toBase64Url(await sha256(bytes)),
    };
    validateManifest(manifest);
    return {
        version: 1,
        chunks,
        manifest: await sealContent(key, context(base, 'artifact.manifest', 1), utf8(canonicalize(manifest))),
    };
}
function validateManifest(m) {
    exactKeys(m, ['version', 'name', 'mime', 'bytes', 'chunks', 'digest']);
    if (m.version !== 1 ||
        typeof m.name !== 'string' ||
        m.name.length > 4096 ||
        typeof m.mime !== 'string' ||
        m.mime.length > 256 ||
        !Number.isSafeInteger(m.bytes) ||
        m.bytes < 0 ||
        m.bytes > ARTIFACT_CHUNK_BYTES * MAX_ARTIFACT_CHUNKS ||
        m.chunks !== Math.ceil(m.bytes / ARTIFACT_CHUNK_BYTES) ||
        typeof m.digest !== 'string' ||
        fromBase64Url(m.digest).length !== 32)
        throw new Error('invalid artifact manifest');
}
/** Full-buffer convenience API. Large media should use authenticated record-wise reads. */
export async function openArtifact(key, base, encrypted) {
    exactKeys(encrypted, ['version', 'manifest', 'chunks']);
    if (encrypted.version !== 1 ||
        !Array.isArray(encrypted.chunks) ||
        encrypted.chunks.length > MAX_ARTIFACT_CHUNKS)
        throw new Error('invalid artifact');
    const manifest = JSON.parse(fromUtf8(await openContent(key, context(base, 'artifact.manifest', 1), encrypted.manifest)));
    validateManifest(manifest);
    if (encrypted.chunks.length !== manifest.chunks)
        throw new Error('artifact truncated');
    const bytes = new Uint8Array(manifest.bytes);
    for (let i = 0; i < encrypted.chunks.length; i++) {
        const chunk = encrypted.chunks[i];
        if (!chunk)
            throw new Error('missing artifact record');
        const opened = await openContent(key, context(base, 'artifact.chunk', i + 1), chunk);
        if (opened.length !== Math.min(ARTIFACT_CHUNK_BYTES, manifest.bytes - i * ARTIFACT_CHUNK_BYTES))
            throw new Error('invalid artifact record length');
        bytes.set(opened, i * ARTIFACT_CHUNK_BYTES);
    }
    if (!bytesEqual(await sha256(bytes), fromBase64Url(manifest.digest)))
        throw new Error('artifact digest mismatch');
    return { bytes, name: manifest.name, mime: manifest.mime };
}
/** Authenticate the manifest before using its sizes to drive bounded network reads. */
export async function openArtifactManifest(key, base, box) {
    const manifest = JSON.parse(fromUtf8(await openContent(key, context(base, 'artifact.manifest', 1), box)));
    validateManifest(manifest);
    return manifest;
}
/** Pilot endpoint memory budget. Record transport remains capped at 1 MiB plaintext. */
export const ARTIFACT_TRANSPORT_MAX_BYTES = 32 * ARTIFACT_CHUNK_BYTES;
export function artifactRecords(base, encrypted) {
    // Publish manifest last: readers never observe an intentionally incomplete upload.
    return [
        ...encrypted.chunks.map((box, i) => ({ context: context(base, 'artifact.chunk', i + 1), box })),
        { context: context(base, 'artifact.manifest', 1), box: encrypted.manifest },
    ];
}
export async function readArtifact(key, base, transport) {
    async function get(kind, seq) {
        const expected = context(base, kind, seq);
        const record = await transport.get(expected);
        exactKeys(record, ['context', 'box']);
        if (canonicalize(record.context) !== canonicalize(expected))
            throw new Error('wrong artifact context');
        // Bound before decoding; one 1-MiB record plus padding/tag.
        if (record.box.ciphertext.length > 1_400_000)
            throw new Error('artifact record too large');
        return record.box;
    }
    const manifest = await get('artifact.manifest', 1);
    const metadata = await openArtifactManifest(key, base, manifest);
    if (metadata.bytes > ARTIFACT_TRANSPORT_MAX_BYTES)
        throw new Error('artifact too large');
    const chunks = [];
    for (let i = 0; i < metadata.chunks; i++)
        chunks.push(await get('artifact.chunk', i + 1));
    return openArtifact(key, base, { version: 1, manifest, chunks });
}
//# sourceMappingURL=artifact-content.js.map